import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSente, SenteApiError } from '../hooks/useSente';

// New Account modal (shared). With an `identity` prop the account attaches to it (IdentityDetail);
// without one the server auto-provisions the backing identity — accounts are first-order, the
// identity is plumbing. `newMode` toggles register (Sente creates the account) / connect (delegate
// an account the customer owns).
interface NewAccountModalProps {
  open: boolean;
  onClose: () => void;
  identity?: { id: string; email: string } | null;
  initialMode?: 'register' | 'connect';
  prefillUrl?: string;
}

const NewAccountModal: React.FC<NewAccountModalProps> = ({ open, onClose, identity, initialMode = 'register', prefillUrl = '' }) => {
  const navigate = useNavigate();
  const { createRegistration, createConnection } = useSente();

  const [newMode, setNewMode] = useState<'register' | 'connect'>(initialMode);
  const [newAppUrl, setNewAppUrl] = useState(prefillUrl);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newTotpSeed, setNewTotpSeed] = useState('');
  const [newConfirmBeforeSubmit, setNewConfirmBeforeSubmit] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<React.ReactNode>(null);

  // Fresh fields every time the modal opens (also picks up a changed initialMode/prefillUrl).
  useEffect(() => {
    if (!open) return;
    setNewMode(initialMode);
    setNewAppUrl(prefillUrl);
    setNewUsername('');
    setNewPassword('');
    setNewTotpSeed('');
    setNewConfirmBeforeSubmit(false);
    setIsCreating(false);
    setCreateError(null);
  }, [open, initialMode, prefillUrl]);

  const limitError = (e: any): React.ReactNode => {
    if (e instanceof SenteApiError && e.code === 'RUN_LIMIT_EXCEEDED')
      return 'Daily run limit reached for your org — try again tomorrow.';
    if (e instanceof SenteApiError && e.code === 'IDENTITY_LIMIT_EXCEEDED')
      return (
        <>
          Identity limit reached for your plan.{' '}
          <Link to="/billing" className="underline font-medium">Upgrade</Link>
        </>
      );
    return e.message || 'Something went wrong';
  };

  const handleCreate = async () => {
    if (!newAppUrl.trim()) {
      setCreateError('Enter the app signup URL.');
      return;
    }
    if (newMode === 'connect' && (!newUsername.trim() || !newPassword)) {
      setCreateError('Connecting an account you own needs its username and password.');
      return;
    }
    setIsCreating(true);
    setCreateError(null);
    try {
      const { run } =
        newMode === 'connect'
          ? await createConnection({
              ...(identity ? { identityId: identity.id } : {}),
              appUrl: newAppUrl.trim(),
              credentials: {
                username: newUsername.trim(),
                password: newPassword,
                ...(newTotpSeed.trim() ? { totpSeed: newTotpSeed.trim() } : {}),
              },
            }).then((r) => ({ run: r.run }))
          : await createRegistration({
              ...(identity ? { identityId: identity.id } : {}),
              appUrl: newAppUrl.trim(),
              credentials:
                newUsername.trim() || newPassword
                  ? { username: newUsername.trim() || undefined, password: newPassword || undefined }
                  : undefined,
              confirmBeforeSubmit: newConfirmBeforeSubmit || undefined,
            });
      navigate(`/runs/${run.id}`);
    } catch (e: any) {
      setCreateError(limitError(e));
      setIsCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !isCreating && onClose()}>
      <div
        className="w-full max-w-md p-6 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">New Account</h2>

        {/* Register (create a new account) vs Connect (delegate one you already own). */}
        <div className="inline-flex mb-3 rounded-lg border border-gray-200 dark:border-slate-700 p-0.5 bg-gray-50 dark:bg-slate-800/50">
          {(['register', 'connect'] as const).map((m) => (
            <button
              key={m}
              onClick={() => { setNewMode(m); setCreateError(null); }}
              disabled={isCreating}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                newMode === m
                  ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200'
              }`}
            >
              {m === 'register' ? 'Register new' : 'Connect existing'}
            </button>
          ))}
        </div>

        <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">
          {newMode === 'connect' ? (
            identity ? (
              <>Connect an app account you already own to <span className="font-mono">{identity.email}</span>. Give the
              agent its credentials — it logs in and keeps the account alive (re-login, TOTP). Credentials are stored
              write-only; revoke access anytime.</>
            ) : (
              <>Connect an app account you already own. Give the agent its credentials — it logs in and keeps the
              account alive (re-login, TOTP). A fresh identity (the account's own inbox) is created automatically.
              Credentials are stored write-only; revoke access anytime.</>
            )
          ) : identity ? (
            <>Register <span className="font-mono">{identity.email}</span> with an app. The managed browser signs up,
            completes email verification from this inbox, and keeps the account alive. You can watch it live.</>
          ) : (
            <>Register with an app under a fresh identity, created automatically for this account. The managed browser
            signs up, completes email verification from the new inbox, and keeps the account alive. You can watch it
            live.</>
          )}
        </p>

        {createError && (
          <div className="mt-4 mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {createError}
          </div>
        )}

        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1 mt-4">
          {newMode === 'connect' ? 'App login URL' : 'App signup URL'}
        </label>
        <input
          type="url"
          value={newAppUrl}
          onChange={e => setNewAppUrl(e.target.value)}
          placeholder={newMode === 'connect' ? 'https://app.example.com/login' : 'https://app.example.com/signup'}
          className="w-full mb-4 px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Username{newMode === 'connect'
                ? <span className="text-gray-400 dark:text-slate-500 font-normal"> / email</span>
                : <span className="text-gray-400 dark:text-slate-500 font-normal"> (optional)</span>}
            </label>
            <input
              type="text"
              value={newUsername}
              onChange={e => setNewUsername(e.target.value)}
              placeholder={newMode === 'connect' ? 'the login you use' : ''}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Password{newMode === 'connect'
                ? ''
                : <span className="text-gray-400 dark:text-slate-500 font-normal"> (optional)</span>}
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder={newMode === 'connect' ? 'your account password' : 'generated if blank'}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {newMode === 'connect' ? (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
              Authenticator (TOTP) seed <span className="text-gray-400 dark:text-slate-500 font-normal">(optional)</span>
            </label>
            <input
              type="password"
              value={newTotpSeed}
              onChange={e => setNewTotpSeed(e.target.value)}
              placeholder="base32 secret or otpauth:// URI"
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="block text-xs text-gray-400 dark:text-slate-500 mt-1">
              If the account uses an authenticator app, paste its setup secret so re-login clears 2FA without you.
            </span>
          </div>
        ) : (
          <label className="flex items-start gap-2 mb-6 cursor-pointer">
            <input
              type="checkbox"
              checked={newConfirmBeforeSubmit}
              onChange={e => setNewConfirmBeforeSubmit(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-700 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-slate-300">
              Confirm before submit
              <span className="block text-xs text-gray-400 dark:text-slate-500 font-normal">
                The agent fills the form but pauses before the final submit — you click it yourself in the live
                view (so you accept the app's terms), then resume the run.
              </span>
            </span>
          </label>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isCreating}
            className="px-4 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !newAppUrl.trim() || (newMode === 'connect' && (!newUsername.trim() || !newPassword))}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
          >
            {isCreating ? 'Starting…' : newMode === 'connect' ? 'Connect' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewAccountModal;
